
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Helmet } from "react-helmet";
import { useCatalogs, useCatalogPages } from "@/hooks/useCatalogs";
import { CatalogPreviewModal } from "@/components/CatalogPreviewModal";
import { useState } from "react";
import { AboutCreator } from "@/components/AboutCreator";
import { useAdmin } from "@/providers/AdminProvider";
import { UploadPageForm } from "@/components/UploadPageForm";

const CatalogDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { isAdmin } = useAdmin();
  
  const { data: catalogs } = useCatalogs();
  const { data: pages } = useCatalogPages(id!);
  const catalog = catalogs?.find(c => c.id === id);

  if (!catalog) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{catalog?.title || 'Vintage Coach Catalog'}</title>
        <meta 
          name="description" 
          content={catalog ? 
            `View the complete ${catalog.title} Coach catalog. Browse original catalog pages and discover vintage Coach bags from this historical collection.` :
            'Browse this vintage Coach catalog to discover historical bag designs and collections.'
          } 
        />
      </Helmet>
      <main className="min-h-screen w-full flex flex-col">
        <div className="flex-grow max-w-7xl mx-auto">
          <div className="px-4 pt-20 pb-12">
            <Header />

            <div className="space-y-8 mt-8">
              <h2 className="text-2xl font-display text-center">{catalog.title}</h2>

              {isAdmin && (
                <div className="max-w-xl mx-auto mb-8 p-6 border rounded-lg bg-background">
                  <UploadPageForm catalogId={id!} />
                </div>
              )}

              <div className="space-y-8">
                {pages?.map((page) => (
                  <div 
                    key={page.id}
                    className="cursor-pointer max-w-[70%] mx-auto"
                    onClick={() => setSelectedImage(page.thumbnail_url)}
                  >
                    {page.thumbnail_url ? (
                      <div className="space-y-4">
                        <div className="relative">
                          <img
                            src={page.thumbnail_url}
                            alt={`Page ${page.page_number}`}
                            className="w-full object-contain rounded-lg shadow-lg"
                          />
                          <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1.5 rounded-full text-sm">
                            Page {page.page_number}
                          </div>
                        </div>
                        {page.page_title && (
                          <p className="mt-2 text-center text-muted-foreground">{page.page_title}</p>
                        )}
                      </div>
                    ) : (
                      <div className="w-full aspect-[8.5/11] bg-muted flex items-center justify-center rounded-lg">
                        <p className="text-muted-foreground">No preview</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <AboutCreator />
      
      {selectedImage && (
        <CatalogPreviewModal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          catalogTitle={catalog.title}
          imageUrl={selectedImage}
        />
      )}
    </>
  );
};

export default CatalogDetailPage;
